import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@material-ui/core/styles';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { LabeledCount } from '../../../../../../../api/teamMetricsApi';

const useStyles = makeStyles((theme) => ({
  container: {
    marginBottom: theme.spacing(4),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(3),
  },
  chartCard: {
    padding: theme.spacing(2),
    border: '1px solid #E2E8F0',
    borderRadius: 4,
    backgroundColor: '#fff',
  },
  chartTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: '#4A5568',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: theme.spacing(2),
  },
  chartContainer: {
    height: 200,
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#8E9299',
    fontSize: 12,
  },
}));

const SEVERITY_COLORS: Record<string, string> = {
  'SEV 1': '#F56565',
  'SEV 2': '#ED8936',
  'SEV 3': '#ECC94B',
  'SEV 4': '#9F7AEA',
  'TBD': '#A0AEC0',
};

const CRITICALITY_COLORS: Record<string, string> = {
  'Tier 1': '#FC8181',
  'Tier 2': '#F6AD55',
  'Tier 3': '#68D391',
  '#N/A': '#A0AEC0',
};

const IMPACT_COLORS: Record<string, string> = {
  'Outage': '#FC8181',
  'Major Degradation': '#F6AD55',
  'Minor Degradation': '#68D391',
  'Near Miss': '#63B3ED',
};

const DEFAULT_COLORS = ['#3182CE', '#38A169', '#D69E2E', '#E53E3E', '#805AD5', '#00B5D8'];

const SEVERITY_ORDER = ['SEV 1', 'SEV 2', 'SEV 3', 'SEV 4'];
const CRITICALITY_ORDER = ['Tier 1', 'Tier 2', 'Tier 3'];
const IMPACT_ORDER = ['Outage', 'Major Degradation', 'Minor Degradation', 'Near Miss'];

function sortByPriority(data: LabeledCount[], priorityOrder: string[]): LabeledCount[] {
  return [...data].sort((a, b) => {
    const aIndex = priorityOrder.indexOf(a.label);
    const bIndex = priorityOrder.indexOf(b.label);
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return a.label.localeCompare(b.label);
  });
}

interface DistributionChartProps {
  title: string;
  data: LabeledCount[];
  colorMap?: Record<string, string>;
}

const DistributionChart: React.FC<DistributionChartProps> = ({ title, data, colorMap }) => {
  const classes = useStyles();

  const chartData = data.map((item, index) => ({
    name: item.label,
    value: item.count,
    color: colorMap?.[item.label] ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length],
  }));

  const hasData = chartData.length > 0 && chartData.some((d) => d.value > 0);

  return (
    <Paper className={classes.chartCard} elevation={0}>
      <Typography className={classes.chartTitle}>{title}</Typography>
      <Box className={classes.chartContainer}>
        {!hasData ? (
          <Box className={classes.emptyState}>
            <Typography>No data available</Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 5, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal vertical={false} stroke="#F1F1F1" />
              <XAxis
                type="number"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#8E9299' }}
                allowDecimals={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#4A5568' }}
                width={90}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 4,
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
                labelStyle={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}
                formatter={(value: number) => [value, 'Incidents']}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={24}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Paper>
  );
};

interface IncidentDistributionChartsProps {
  severityDistribution: LabeledCount[];
  criticalityDistribution: LabeledCount[];
  productImpactDistribution: LabeledCount[];
}

export const IncidentDistributionCharts: React.FC<IncidentDistributionChartsProps> = ({
  severityDistribution,
  criticalityDistribution,
  productImpactDistribution,
}) => {
  const classes = useStyles();

  const sortedSeverity = sortByPriority(severityDistribution, SEVERITY_ORDER);
  const sortedCriticality = sortByPriority(criticalityDistribution, CRITICALITY_ORDER);
  const sortedImpact = sortByPriority(productImpactDistribution, IMPACT_ORDER);

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography className={classes.title}>Incident Distributions</Typography>
      </Box>
      <Box className={classes.grid}>
        <DistributionChart
          title="By Severity"
          data={sortedSeverity}
          colorMap={SEVERITY_COLORS}
        />
        <DistributionChart
          title="By Criticality"
          data={sortedCriticality}
          colorMap={CRITICALITY_COLORS}
        />
        <DistributionChart
          title="By Product Impact"
          data={sortedImpact}
          colorMap={IMPACT_COLORS}
        />
      </Box>
    </Box>
  );
};

export default IncidentDistributionCharts;
